import React, { useEffect, useRef } from "react"
import { Box } from "@mui/material"
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js"
import { Line } from "react-chartjs-2"
import { TimeScale } from "chart.js"
import "chartjs-adapter-date-fns"

// Registrar los componentes de Chart.js
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

// Definir los rangos de temperatura y sus colores
const temperatureRanges = [
  { min: -10, max: 0, color: "rgba(0, 191, 255, 0.1)" }, // Muy frío - azul claro
  { min: 0, max: 10, color: "rgba(135, 206, 250, 0.1)" }, // Frío - celeste
  { min: 10, max: 20, color: "rgba(152, 251, 152, 0.1)" }, // Fresco - verde claro
  { min: 20, max: 25, color: "rgba(255, 255, 0, 0.1)" }, // Templado - amarillo
  { min: 25, max: 30, color: "rgba(255, 165, 0, 0.1)" }, // Cálido - naranja
  { min: 30, max: 40, color: "rgba(255, 69, 0, 0.1)" }, // Caliente - rojo
]

// Modificar la función TemperatureChart para agregar un id al contenedor
export function TemperatureChart({ data }) {
  const chartRef = useRef(null)

  // Plugin personalizado para dibujar las franjas de colores en el fondo
  const backgroundColorsPlugin_tem = {
    id: "backgroundColors",
    beforeDraw: (chart) => {
      const { ctx, chartArea, scales } = chart
      if (!chartArea) return // Asegurarse de que chartArea esté disponible
      const { top, bottom, left, right } = chartArea
      const yScale = scales.y
  
      if (!yScale) return
  
      temperatureRanges.forEach((range) => {
        const yMin = yScale.getPixelForValue(range.max)
        const yMax = yScale.getPixelForValue(range.min)
  
        if (isNaN(yMin) || isNaN(yMax)) return
  
        ctx.save()
        ctx.fillStyle = range.color
        ctx.fillRect(left, yMin, right - left, yMax - yMin)
        ctx.restore()
      })
    },
  }

  const chartData = {
    labels: data.labels.map(t => t * 1000), // Asegúrate que estos sean timestamps válidos
    datasets: [
      {
        label: "Temperatura (°C)",
        data: data.temperature.map(t => t / 100),
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { left: 0, right: 0, top: 10, bottom: 0 },
    },
    plugins: {
      legend: { position: "top" },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y}%`,
        },
      },
    },
    scales: {
      y: {
        min: -10,
        max: 40,
        title: {
          display: true,
          text: "Temperature (°C)",
        },
      },
      x: {
        type: "time",
        time: {
          unit: "minute",
          tooltipFormat: "dd/MM/yyyy HH:mm",
          displayFormats: {
            minute: "dd/MM/yy HH:mm",
          },
        },
        title: {
          display: true,
          text: "Hora",
        },
      },
    },
  }

  useEffect(() => {
    // Registrar el plugin personalizado
    Chart.register(backgroundColorsPlugin_tem)

    return () => {
      // Limpiar el plugin al desmontar
      Chart.unregister(backgroundColorsPlugin_tem)
    }
  }, [])

  return React.createElement(Box, 
    { 
      id: "temperature-chart", 
      sx: { height: "100%", width: "100%" }
    },
    React.createElement(Line, {
      ref: chartRef,
      data: chartData,
      options: {
        ...chartOptions,
        maintainAspectRatio: false,
        responsive: true,
      },
      style: { width: "100%", height: "100%" }
    })
  )
}
